/**
 * React Query hook for image status counts
 * Provides proper caching and optimistic updates for status counts
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { fetchImageStatusCounts } from '@/api/imageApi';
import imageKeys from '@/api/querry_keys/imageKeys';

interface ImageStatusCounts {
  approved: number;
  pending: number;
  rejected: number;
  total: number;
  approved_duration_minutes: number;
  pending_duration_minutes: number;
  rejected_duration_minutes: number;
  total_duration_minutes: number;
}

interface UseImageStatusCountsReturn {
  counts: ImageStatusCounts;
  error: Error | null;
  isLoading: boolean;
  refetch: () => Promise<any>;
  optimisticUpdateCounts: (oldStatus: string | undefined, newStatus: string, duration?: number) => void;
  optimisticDeleteCounts: (status: string, duration?: number) => void;
  optimisticAddCounts: (status: string, duration?: number) => void;
}

interface UseImageStatusCountsProps {
  userEmail?: string;
}

const defaultCounts: ImageStatusCounts = {
  approved: 0,
  pending: 0,
  rejected: 0,
  total: 0,
  approved_duration_minutes: 0,
  pending_duration_minutes: 0,
  rejected_duration_minutes: 0,
  total_duration_minutes: 0,
};

export const useImageStatusCounts = ({ 
  userEmail 
}: UseImageStatusCountsProps = {}): UseImageStatusCountsReturn => {
  const queryClient = useQueryClient();

  // Create query key that includes userEmail for proper caching
  const queryKey = userEmail 
    ? imageKeys.userStatusCounts(userEmail)
    : imageKeys.statusCounts();

  const {
    data = defaultCounts,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      
      try {
        const result = await fetchImageStatusCounts(userEmail);
        
        // Transform the API response to match our interface
        const counts: ImageStatusCounts = {
          approved: result.approved || 0,
          pending: result.pending || 0,
          rejected: result.rejected || 0,
          total: result.total || 0,
          approved_duration_minutes: 0, // Images don't have duration
          pending_duration_minutes: 0,
          rejected_duration_minutes: 0,
          total_duration_minutes: 0,
        };
        
        return counts;
      } catch (error) {
        console.error('[ImageStatusCounts] Fetch failed:', error);
        throw error;
      }
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Optimistic update for status changes
  const optimisticUpdateCounts = useCallback((
    oldStatus: string | undefined, 
    newStatus: string, 
    _duration: number = 0
  ) => {
    queryClient.setQueryData<ImageStatusCounts>(queryKey, (prevCounts) => {
      if (!prevCounts) return defaultCounts;
      
      const newCounts = { ...prevCounts };
            
      // Remove from old status if it exists
      if (oldStatus && oldStatus !== 'all') {
        const oldStatusKey = oldStatus as keyof typeof newCounts;
        if (typeof newCounts[oldStatusKey] === 'number') {
          newCounts[oldStatusKey] = Math.max(0, newCounts[oldStatusKey] - 1);
        }
      }
      
      // Add to new status
      if (newStatus && newStatus !== 'all') {
        const newStatusKey = newStatus as keyof typeof newCounts;
        if (typeof newCounts[newStatusKey] === 'number') {
          newCounts[newStatusKey] = newCounts[newStatusKey] + 1;
        }
      }
      
      return newCounts;
    });
  }, [queryClient, queryKey]);

  // Optimistic delete - decreases count for given status
  const optimisticDeleteCounts = useCallback((status: string, _duration: number = 0) => {
    queryClient.setQueryData<ImageStatusCounts>(queryKey, (prevCounts) => {
      if (!prevCounts) return defaultCounts;
      
      const newCounts = { ...prevCounts };
      
      if (status && status !== 'all') {
        const statusKey = status as keyof typeof newCounts;
        if (typeof newCounts[statusKey] === 'number') {
          newCounts[statusKey] = Math.max(0, newCounts[statusKey] - 1);
        }
      }
      
      // Always decrease total
      newCounts.total = Math.max(0, newCounts.total - 1);
      
      return newCounts;
    });
  }, [queryClient, queryKey]);

  // Optimistic add - increases count for given status
  const optimisticAddCounts = useCallback((status: string, _duration: number = 0) => {
    queryClient.setQueryData<ImageStatusCounts>(queryKey, (prevCounts) => {
      if (!prevCounts) return defaultCounts;
      
      const newCounts = { ...prevCounts };
      
      if (status && status !== 'all') {
        const statusKey = status as keyof typeof newCounts;
        if (typeof newCounts[statusKey] === 'number') {
          newCounts[statusKey] = newCounts[statusKey] + 1;
        }
      }
      
      // Always increase total
      newCounts.total = newCounts.total + 1;
      
      return newCounts;
    });
  }, [queryClient, queryKey]);

  return {
    counts: data,
    error,
    isLoading,
    refetch,
    optimisticUpdateCounts,
    optimisticDeleteCounts,
    optimisticAddCounts,
  };
};